import { useState } from "react";
import { useTranslation } from "react-i18next";

import { CallsChart } from "@/components/calls-chart";
import { LoadErrorCard, LoadingCard } from "@/components/load-state";
import { MeasureSwitch } from "@/components/segmented-control";
import { useAdminMcpIssues, useAdminMcpUsage } from "@/lib/api";
import type { ChartMeasure } from "@/lib/calls-chart";
import { usePeriod } from "@/lib/period";
import { cn } from "@/lib/utils";

import { ChartSection, EmptyChart, SectionHeading, StaleNote } from "../parts";
import { loadState } from "../utils";
import { IssueFilterBar, IssueTable } from "./issue-table";

/**
 * Statistieken's Fouten: over the period, the failed calls per day, as a
 * count or as a percentage of that day's calls ("Aantal | Percentage"), then
 * the issues they belong to, each with its status, for Velta to analyse.
 * Every failure counts: there is no threshold. The page opens with its filter
 * bar in the tinted box: the period and the list's filters.
 */
export function FailuresPage() {
  const { t } = useTranslation();
  const [days, setDays] = usePeriod();
  const [measure, setMeasure] = useState<ChartMeasure>("count");
  const usageQuery = useAdminMcpUsage(days);
  const issuesQuery = useAdminMcpIssues(days);
  const usage = usageQuery.data;
  const issues = issuesQuery.data;
  const state = loadState(usageQuery, issuesQuery);
  const retry = () => {
    void usageQuery.refetch();
    void issuesQuery.refetch();
  };
  const updating = usageQuery.isPlaceholderData || issuesQuery.isPlaceholderData;

  return (
    <div className="space-y-8">
      <IssueFilterBar issues={issues?.issues ?? []} days={days} onDaysChange={setDays} />
      {usageQuery.isLoading || issuesQuery.isLoading ? (
        <LoadingCard />
      ) : state === "failed" || !usage || !issues ? (
        <LoadErrorCard message={t("issues.loadError")} onRetry={retry} />
      ) : (
        <div className={cn("space-y-8 transition-opacity", updating && "opacity-60")}>
          {state === "stale" && <StaleNote onRetry={retry} />}
          <ChartSection
            title={t("issues.perDay")}
            aside={
              usage.totals.errors > 0 && <MeasureSwitch value={measure} onChange={setMeasure} />
            }
          >
            {usage.totals.errors === 0 ? (
              <EmptyChart>{t("issues.noFailures", { count: usage.days })}</EmptyChart>
            ) : (
              <CallsChart data={usage.per_day} series="errors" measure={measure} />
            )}
          </ChartSection>
          <section aria-labelledby="failures-issues">
            <SectionHeading id="failures-issues">{t("issues.list.title")}</SectionHeading>
            {issues.issues.length === 0 ? (
              <p className="text-muted rounded-lg border py-8 text-center text-sm">
                {t("issues.noFailures", { count: issues.days })}
              </p>
            ) : (
              <IssueTable issues={issues.issues} days={days} />
            )}
          </section>
        </div>
      )}
    </div>
  );
}
